import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as assistantApi from '@/api/assistant';
import { getAssistantFallbackMessage } from '@/lib/assistantFallback';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolCalls?: Array<{
    name: string;
    result?: unknown;
    approvalId?: string;
    runId?: string;
  }>;
}

export interface AssistantContext {
  currentPage: string;
  activeTab: string;
  selectedItems?: string[];
}

interface AssistantState {
  isOpen: boolean;
  messages: Message[];
  conversationId: string | null;
  conversationTeamId: string | null;
  isLoading: boolean;
  isStreaming: boolean;
  context: AssistantContext;
}

interface AssistantActions {
  toggle: () => void;
  open: () => void;
  close: () => void;
  sendMessage: (teamId: string, content: string) => Promise<void>;
  hydrateConversation: (teamId: string) => Promise<void>;
  clearConversation: (teamId: string) => Promise<void>;
  updateContext: (ctx: Partial<AssistantContext>) => void;
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
}

type AssistantStore = AssistantState & AssistantActions;

function replacePlaceholderMessage(
  messages: Message[],
  placeholderId: string,
  updater: (message: Message) => Message | null,
): Message[] {
  return messages.flatMap((message) => {
    if (message.id !== placeholderId) {
      return [message];
    }

    const nextMessage = updater(message);
    return nextMessage ? [nextMessage] : [];
  });
}

const initialState: AssistantState = {
  isOpen: false,
  messages: [],
  conversationId: null,
  conversationTeamId: null,
  isLoading: false,
  isStreaming: false,
  context: {
    currentPage: 'dashboard',
    activeTab: 'dashboard',
  },
};

export const useAssistantStore = create<AssistantStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      toggle: () => set((state) => ({ isOpen: !state.isOpen })),

      open: () => set({ isOpen: true }),

      close: () => set({ isOpen: false }),

      addMessage: (message) => {
        const id = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const newMessage: Message = {
          ...message,
          id,
          timestamp: new Date(),
        };
        set((state) => ({
          messages: [...state.messages, newMessage],
        }));
      },

      sendMessage: async (teamId: string, content: string) => {
        const { addMessage, context } = get();
        let { conversationId, conversationTeamId } = get();

        // Prevent cross-team memory reuse when switching teams.
        if (conversationId && conversationTeamId && conversationTeamId !== teamId) {
          set({
            messages: [],
            conversationId: null,
            conversationTeamId: teamId,
          });
          conversationId = null;
          conversationTeamId = teamId;
        }

        // Add user message immediately
        addMessage({ role: 'user', content });

        // Create placeholder assistant message
        const placeholderId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const placeholderMessage: Message = {
          id: placeholderId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          toolCalls: [],
        };
        set((state) => ({
          messages: [...state.messages, placeholderMessage],
          isLoading: true,
          isStreaming: true,
        }));

        try {
          let didReceiveTerminalEvent = false;

          await assistantApi.streamMessage(
            teamId,
            {
              message: content,
              conversation_id: conversationId,
              context,
            },
            (event) => {
              const state = get();

              if (event.type === 'content') {
                const delta = 'delta' in event ? event.delta : event.text;
                if (!delta) return;

                // Append delta to the placeholder message
                set({
                  messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => ({
                    ...msg,
                    content: msg.content + delta,
                  })),
                });
              } else if (event.type === 'tool_call') {
                set({
                  messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => ({
                    ...msg,
                    toolCalls: [
                      ...(msg.toolCalls || []),
                      { name: event.name },
                    ],
                  })),
                });
              } else if (event.type === 'harness_status') {
                const label = event.status === 'started'
                  ? `Harness started (${event.permission_profile || 'default'})`
                  : `Harness ${event.status}${event.message ? `: ${event.message}` : ''}`;
                set({
                  messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => ({
                    ...msg,
                    toolCalls: [
                      ...(msg.toolCalls || []),
                      { name: label },
                    ],
                  })),
                });
              } else if (event.type === 'harness_tool_call') {
                set({
                  messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => ({
                    ...msg,
                    toolCalls: [
                      ...(msg.toolCalls || []),
                      { name: `Harness: ${event.name}` },
                    ],
                  })),
                });
              } else if (event.type === 'harness_run_done') {
                set({
                  messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => ({
                    ...msg,
                    toolCalls: [
                      ...(msg.toolCalls || []),
                      { name: `Harness run ${event.run_id.slice(0, 8)}` },
                    ],
                  })),
                });
              } else if (event.type === 'harness_approval_required') {
                set({
                  messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => ({
                    ...msg,
                    toolCalls: [
                      ...(msg.toolCalls || []),
                      {
                        name: `Approval required: ${event.tool_name}`,
                        approvalId: event.approval_id,
                        runId: event.run_id,
                        result: { reason: event.reason, arguments: event.arguments },
                      },
                    ],
                  })),
                });
              } else if (event.type === 'tool_result') {
                // Update the last tool call with its result
                set({
                  messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => {
                    const toolCalls = [...(msg.toolCalls || [])];
                    // Find last matching tool call (reverse search)
                    let idx = -1;
                    for (let i = toolCalls.length - 1; i >= 0; i--) {
                      if (toolCalls[i].name === event.name) { idx = i; break; }
                    }
                    if (idx >= 0) {
                      toolCalls[idx] = { ...toolCalls[idx], result: event.result };
                    }
                    return { ...msg, toolCalls };
                  }),
                });
              } else if (event.type === 'done') {
                didReceiveTerminalEvent = true;
                set({
                  isStreaming: false,
                  isLoading: false,
                  conversationId: event.conversation_id,
                  conversationTeamId: teamId,
                });
              } else if (event.type === 'error') {
                didReceiveTerminalEvent = true;
                // Prefer the server's error message (which includes differentiated messages
                // for rate limiting, service unavailable, timeouts, etc.)
                const serverMessage = event.message;
                set({
                  messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => ({
                    ...msg,
                    content: msg.content || serverMessage || getAssistantFallbackMessage(),
                  })),
                  isStreaming: false,
                  isLoading: false,
                });
              }
            },
          );

          if (!didReceiveTerminalEvent) {
            const state = get();
            set({
              messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => {
                if (!msg.content && (!msg.toolCalls || msg.toolCalls.length === 0)) {
                  return { ...msg, content: getAssistantFallbackMessage() };
                }
                return msg;
              }),
              isStreaming: false,
              isLoading: false,
            });
          }
        } catch (error) {
          const state = get();
          set({
            messages: replacePlaceholderMessage(state.messages, placeholderId, (msg) => {
              if (!msg.content && (!msg.toolCalls || msg.toolCalls.length === 0)) {
                return { ...msg, content: getAssistantFallbackMessage() };
              }
              return msg;
            }),
            isStreaming: false,
            isLoading: false,
          });
          throw error;
        }
      },

      hydrateConversation: async (teamId: string) => {
        const state = get();
        const { conversationId, conversationTeamId, isStreaming, isLoading } = state;

        // Guard against stale cross-team persisted state.
        if (conversationTeamId && conversationTeamId !== teamId) {
          set({
            messages: [],
            conversationId: null,
            conversationTeamId: null,
          });
          return;
        }

        if (!conversationId || isStreaming || isLoading) return;
        if (state.messages.length > 0) return;

        try {
          const history = await assistantApi.getConversationHistory(teamId, conversationId);
          const hydrated: Message[] = history.messages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
            toolCalls: msg.tool_calls,
          }));

          set({
            conversationId: history.conversation_id,
            conversationTeamId: teamId,
            messages: hydrated,
          });
        } catch {
          // Conversation may have been deleted/expired server-side.
          set({
            messages: [],
            conversationId: null,
            conversationTeamId: null,
          });
        }
      },

      clearConversation: async (teamId: string) => {
        const { conversationId } = get();

        if (conversationId) {
          try {
            await assistantApi.clearConversation(teamId, conversationId);
          } catch {
            // Ignore errors clearing conversation
          }
        }

        set({
          messages: [],
          conversationId: null,
          conversationTeamId: null,
        });
      },

      updateContext: (ctx) => {
        set((state) => ({
          context: { ...state.context, ...ctx },
        }));
      },
    }),
    {
      name: 'tc-insights-assistant',
      partialize: (state) => ({
        isOpen: state.isOpen,
        conversationId: state.conversationId,
        conversationTeamId: state.conversationTeamId,
        // Don't persist messages - they're session-specific
      }),
      onRehydrateStorage: () => {
        return (state?: AssistantStore) => {
          // Clear any stale loading state on rehydration
          if (state) {
            state.isLoading = false;
            state.isStreaming = false;
            state.messages = [];
          }
        };
      },
    }
  )
);

// Export convenience hooks
export const useAssistantOpen = () => useAssistantStore((state) => state.isOpen);
export const useAssistantMessages = () => useAssistantStore((state) => state.messages);
